import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { SectionBadge } from '../../components/SectionHeading'
import { ArrowCircleButton } from '../../components/Button'

import { useEffect, useState } from 'react'
import { bookingService } from '../../api/services/bookingService'
import type { Vehicle } from '../../types/booking'

type FleetVehicleCardProps = {
  category: string
  name: string
  description?: string
  image: string
  passengers: number
  luggage: number
  features?: string[]
}

function FleetVehicleCard({
  category,
  name,
  description = "Comfortable and modern vehicle for your journey.",
  image,
  passengers,
  luggage,
  features = [],
}: FleetVehicleCardProps) {
  const navigate = useNavigate()
  return (
    <motion.article 
      onClick={() => navigate(`/fleets?vehicle=${encodeURIComponent(name)}`)}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
      }}
      className="cursor-pointer overflow-hidden rounded-[24px] bg-[#ECECEC] shadow-[0_1px_18px_rgba(0,0,0,0.06)] transition-all hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative h-[190px] w-full bg-[#BFD86E] sm:h-[200px] rounded-b-[20px]">
        <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-[12px] bg-[#B8B8A1] px-3 py-1.5 shadow-[0_1px_6px_rgba(0,0,0,0.08)]">
          <span className="h-2.5 w-2.5 rounded-full bg-[#A5A5A5]" />
          <span className="font-host text-[13px] font-medium leading-none text-[#111111]">
            {category}
          </span>
        </div>

       <div className="flex h-full items-center justify-center pb-2 pt-10">
        <div className="flex h-[145px] w-[290px] items-center justify-center">
          <img
            src={image}
            alt={name}
            className="h-full w-full object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.23)]"
          />
        </div>
      </div>
      </div>

      <div className="px-4 pb-4 pt-4 sm:px-5 rounded-lg">
        <h3 className="font-host text-[18px] font-medium leading-[1.15] text-[#1F1F1F]">
          {name}
        </h3>

        <p className="mt-1.5 max-w-[250px] font-host text-[13px] leading-[1.38] text-[#6A6A6A]">
          {description}
        </p>

        <div className="mt-3 flex items-center gap-5 text-[#3D3D3D]">
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] leading-none">•</span>
            <img
              src="/assets/images/icon-adult.png"
              alt=""
              className="h-[15px] w-[15px] object-contain opacity-75"
            />
            <span className="font-host text-[13px]">{passengers}</span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[14px] leading-none">•</span>
            <img
              src="/assets/images/icon-child.png"
              alt=""
              className="h-[15px] w-[15px] object-contain opacity-75"
            />
            <span className="font-host text-[13px]">{luggage}</span>
          </div>
        </div>

        <div className="mt-3 flex flex-col gap-2">
          {features.map((feature) => (
            <div key={feature} className="flex items-center gap-2">
               <span className="h-2.5 w-2.5 rounded-full bg-[#363636]" />
              <span className="font-host text-[13px] font-medium text-[#474747]">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.article>
  )
}

export default function Fleet() {
  const [fleetVehicles, setFleetVehicles] = useState<Vehicle[]>([])

  useEffect(() => {
    bookingService
      .fetchVehicles()
      .then((data) => {
        if (data && data.length > 0) {
          setFleetVehicles(data)
        }
      })
      .catch((err) => {
        console.error('Failed to load fleet vehicles:', err)
      })
  }, [])

  if (fleetVehicles.length === 0) return null

  return (
    <section className="bg-white py-14 sm:py-16 lg:py-20 overflow-hidden">
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.1,
            },
          },
        }}
        className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-[42px]"
      >
        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="mb-7 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
        >
          <div>
            <SectionBadge>Our Fleet</SectionBadge>

            <h2 className="mt-3 font-host text-[28px] font-medium leading-[1.08] tracking-[-0.02em] text-[#343434] sm:text-[32px]">
              Vehicles for Every Journey
            </h2>
          </div>

          <p className="mt-12 max-w-[520px] font-host text-[16px] font-thin leading-[1.45] tracking-[-0.03em] text-[#8A8686]">
            Choose from our range of comfortable and modern vehicles, perfect
            for airport transfers, day trips, ski transfers, and private tours
            across Europe.
          </p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {fleetVehicles.map((vehicle) => (
            <FleetVehicleCard key={vehicle.name} {...vehicle} />
          ))}
        </div>

        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="mt-8 flex items-center justify-center gap-3 sm:mt-10"
        >
          <Link
            to="/fleets"
            className="flex h-[48px] items-center justify-center rounded-full border border-[#CFCFCF] bg-[#F2F2F2] px-8 font-host text-[18px] font-medium text-[#FF6A21] shadow-[0_1px_10px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF6A21] hover:bg-white hover:shadow-md active:scale-[0.98]"
          >
            View All
          </Link>

          <ArrowCircleButton
            to="/fleets"
            aria-label="View all vehicles"
            className="!h-[48px] !w-[48px]"
          />
        </motion.div>
      </motion.div>
    </section>
  )
}